/** @file SlotGrid: one part's slot cards, a row per facet entry (band), each card a PNG drop zone. */
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { useContext } from 'react';
import type { ReactElement, RefObject } from 'react';
import { EditorStoreContext } from '../../state/editorStoreContext';
import type { ArtSlot, FitMode, SlotState } from './artTypes';
import { SlotCard } from './SlotCard';
import { SlotDropZone } from './SlotDropZone';

/** The part's slots, card states by key, the selection, the bundle thumbnails come from, and the card callbacks. */
export type SlotGridProps = {
  slots: ArtSlot[]; states: ReadonlyMap<string, SlotState>; selected: string | null; bundle: LoadedBundle | null;
  maskUrl: string | null; pickerRef: RefObject<HTMLInputElement | null>;
  onSelect: (key: string) => void; onMenu: (key: string, x: number, y: number) => void;
  onFill: (key: string, png: Uint8Array, fit?: FitMode) => void; onError: (message: string) => void;
};

/** Slots grouped by entry, in order. */
function rowsOf(slots: ArtSlot[]): ArtSlot[][] {
  const rows = new Map<number, ArtSlot[]>();
  for (const s of slots) rows.set(s.entry, [...(rows.get(s.entry) ?? []), s]);
  return [...rows.values()];
}

/**
 * Rows of cards (responsive columns); the selected card owns the file picker the Upload item and Enter open; a double
 * click on a card opens its Tile tab (`openTile`).
 */
export function SlotGrid(props: SlotGridProps): ReactElement {
  const { slots, states, selected, bundle, maskUrl, pickerRef } = props;
  const store = useContext(EditorStoreContext);
  if (slots.length === 0) return <p style={{ color: '#a9adb6', padding: 12 }}>Pick a part on the left to see its slots.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12, overflow: 'auto' }}>
      {rowsOf(slots).map((row) => {
        const head = row[0];
        return (
          <section key={head?.entry} aria-label={`entry ${head?.entry ?? 0}`}>
            <h4 style={{ margin: '0 0 6px', fontSize: 12, color: '#c9ccd3' }}>Entry {head?.entry} · {head?.band}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
              {row.map((slot) => (
                <SlotDropZone
                  key={slot.key}
                  slotKey={slot.key}
                  slotSize={slot.size}
                  onFill={(png, fit) => { props.onFill(slot.key, png, fit); }}
                  onError={props.onError}
                  {...(slot.key === selected ? { inputRef: pickerRef } : {})}
                >
                  <SlotCard
                    slot={slot}
                    state={states.get(slot.key) ?? slot.status}
                    selected={slot.key === selected}
                    bundle={bundle}
                    maskUrl={slot.key === selected ? maskUrl : null}
                    onSelect={props.onSelect}
                    onMenu={props.onMenu}
                    onOpen={() => { store?.getState().openTile({ drawable: slot.drawable, entry: slot.entry, tile: slot.tile }); }}
                  />
                </SlotDropZone>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
